import { Suspense, lazy, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { documentTitleFor, PUBLIC_PAGE_TITLES } from "../../constants/pageTitles";
import UserLogin from "../../pages/userLogin/UserLogin";
import ProtectComponent from "../protectComponent/ProtectComponent";
import PageNotFound from "../../pages/pageNotFound/PageNotFound";

const UsersDashboard = lazy(() => import("../../pages/usersDashboard/UsersDashboard"));
const UsersStatistics = lazy(() => import("../../pages/usersStatistics/UsersStatistics"));

const NewUserOrder = lazy(() => import("../../pages/usersOrders/newUserOrder/NewUserOrder"));
const ImportOrdersPage = lazy(() => import("../../pages/usersOrders/importOrders/ImportOrdersPage"));
const ImportRequestsPage = lazy(() => import("../../pages/usersInventory/importRequests/ImportRequestsPage"));

const UserOrdersList = lazy(() => import("../../pages/usersOrders/userOrdersList/UserOrdersList"));

const CancelledOrdersList = lazy(() => import("../../pages/usersOrders/cancelledOrdersList/CancelledOrdersList"));

const NewUserShippingRequest = lazy(() => import("../../pages/usersInventory/newUserShippingRequest/NewUserShippingRequest"));

const UserShippingRequests = lazy(() => import("../../pages/usersInventory/userShippingRequests/UserShippingRequest"));

const UserProductsList = lazy(() => import("../../pages/usersInventory/userProductsList/UserProductsList"));

const IntegrationsHome = lazy(() => import("../../pages/usersIntegrations/IntegrationsHome"));

const StorePage = lazy(() => import("../../pages/usersIntegrations/StorePage"));

const StoreTabRedirect = lazy(() => import("../../pages/usersIntegrations/StoreTabRedirect"));

const StoreProblemsGuide = lazy(() => import("../../pages/usersIntegrations/StoreProblemsGuide"));
const StoreFixes = lazy(() => import("../../pages/usersIntegrations/StoreFixes"));


const NewUserClient = lazy(() => import("../../pages/usersClients/newUserClient/NewUserClient"));

const UserClientsList = lazy(() => import("../../pages/usersClients/userClientsList/UserClientsList"));

const UserWallet = lazy(() => import("../../pages/usersWallets/UserWallet"));

const SubscriptionPlans = lazy(() => import("../../pages/subscriptionPlans/SubscriptionPlans"));

const ShippingCalculator = lazy(() => import("../../pages/usersOrders/shippingCalculator/shippingCalculator"));

const Notifications = lazy(() => import("../../pages/notifications/notifications"));

const SellerHistoryPage = lazy(() => import("../../pages/history/sellerHistoryPage"));

const NewUserSignUp = lazy(() => import("../../pages/userSignUp/newUserSignUp"));

const ForgotPassword = lazy(() => import("../../pages/forgotPassword/ForgotPassword"));

const ResetPassword = lazy(() => import("../../pages/resetPassword/ResetPassword"));

const VerifyEmail = lazy(() => import("../../pages/verifyEmail/VerifyEmail"));

const PendingApproval = lazy(() => import("../../pages/pendingApproval/PendingApproval"));

const ShopifyInstall = lazy(() => import("../../pages/shopifyInstall/ShopifyInstall"));

const Tabao1688 = lazy(() => import("../../pages/usersInventory/Tabao1688/tabao1688"));

const UserSourcingRestockRequests = lazy(() => import("../../pages/usersInventory/userShippingRequests/restockRequest/UserSourcingRestockRequests"));

const UserProfile = lazy(() => import("../../pages/usersProfile/UserProfile"));

const HelpGuide = lazy(() => import("../../pages/help/HelpGuide"));

// IMPORTANT: We import the AppLayout to act as our persistent global wrapper
const AppLayout = lazy(() => import("../appLayout/AppLayout"));


function Routing() {
  const location = useLocation();

  // The browser-tab title follows the route, and so does the canonical address
  // on the public pages. index.html only carries the sign-in page's; without
  // this every page, /signUp included, was titled "Sourship" and claimed to be /.
  useEffect(() => {
    document.title = documentTitleFor(location.pathname);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && PUBLIC_PAGE_TITLES[location.pathname]) {
      canonical.setAttribute("href", `${window.location.origin}${location.pathname}`);
    }
  }, [location.pathname]);

  /**
   * key={location.pathname}: This prop ensures that <ProtectComponent></ProtectComponent>,
   * is reloaded every time (reloading means that the <ProtectComponent></ProtectComponent> constructor rebuilds the states, gives them their default values ​​every
   * time a component is navigated using routes. This can only happen if <ProtectComponent></ProtectComponent> is called (loaded) the first time).
   * Normally, React doesn't load <ProtectComponent></ProtectComponent> every time a component is navigated because it's a shared component.
   * React only reuse it (update it). This isn't appropriate in our case,
   * as the isLogedIn state in <ProtectComponent></ProtectComponent> has a default value of false,
   * to ensure that the component to be displayed doesn't appear until some conditional validation function is activated.
   * This function is located in React's <ProtectComponent></ProtectComponent> useEffect ,
   * and the value of isLogedIn state changes once <ProtectComponent></ProtectComponent>
   * is loaded for the first time (once the application is opened for the first time because <ProtectComponent></ProtectComponent>
   * is loaded for the first time) and thus when moving to the next route the component to be shown will appear directly without
   * activating the useEffect function in <ProtectComponent></ProtectComponent>
   *
   * Using key={location.pathname} will make React consider the <ProtectComponent></ProtectComponent> component a new component,
   * and thus recompose it instead of just updating it.
   */

  return (
    <Suspense fallback={<div className="route-loading" role="status" aria-live="polite">Loading…</div>}>
      <Routes>
        {/* =========================================
            PUBLIC ROUTES (No Sidebar/Header)
            ========================================= */}
        <Route path="/" element={<UserLogin />} />
        <Route path="/signUp" element={<NewUserSignUp />} />
        <Route path="/forgotPassword" element={<ForgotPassword />} />
        <Route path="/resetPassword" element={<ResetPassword />} />
        <Route path="/verifyEmail" element={<VerifyEmail />} />
        {/* After installing the Sourship app on Shopify. Public: the merchant
            may not have signed in yet. */}
        <Route path="/shopifyInstall" element={<ShopifyInstall />} />
  
        {/* =========================================
            PROTECTED ROUTES (Wrapped in AppLayout)
            This stops the screen from flashing and the sidebar from reloading!

            A lapsed plan blocks WRITES, not reading: every page that only
            shows the seller's own data carries doNotCheckMonthlyPayment, and
            only the creation pages (new order, new request, Taobao 1688, new
            client) still send a lapsed seller to the plans page, with the
            reason (utils/planEnded.js). The sidebar no longer locks anything.
            ========================================= */}
        <Route element={<AppLayout />}>
  
          <Route
            path="/dashboard"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UsersDashboard />
              </ProtectComponent>
            }
          />
          <Route
            path="/statistics"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UsersStatistics />
              </ProtectComponent>
            }
          />
          <Route
            path="/subscriptionPlans"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <SubscriptionPlans />
              </ProtectComponent>
            }
          />
          <Route
            path="/pendingApproval"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true} doNotCheckApproval={true}>
                <PendingApproval />
              </ProtectComponent>
            }
          />
          <Route
            path="/newOrder"
            element={
              <ProtectComponent key={location.pathname}>
                <NewUserOrder />
              </ProtectComponent>
            }
          />
          {/* Creates and pays orders, so it keeps the plan check like /newOrder. */}
          <Route
            path="/importOrders"
            element={
              <ProtectComponent key={location.pathname}>
                <ImportOrdersPage />
              </ProtectComponent>
            }
          />
          {/* Sends sourcing requests (spends credits), so it keeps the plan check like /newShippingRequest. */}
          <Route
            path="/importRequests"
            element={
              <ProtectComponent key={location.pathname}>
                <ImportRequestsPage />
              </ProtectComponent>
            }
          />
          <Route
            path="/ordersList"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserOrdersList />
              </ProtectComponent>
            }
          />
          <Route
            path="/cancelledOrders"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <CancelledOrdersList />
              </ProtectComponent>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserWallet />
              </ProtectComponent>
            }
          />
          <Route
            path="/shippingCalculator"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <ShippingCalculator />
              </ProtectComponent>
            }
          />
          <Route
            path="/newShippingRequest"
            element={
              <ProtectComponent key={location.pathname}>
                <NewUserShippingRequest />
              </ProtectComponent>
            }
          />
          <Route
            path="/shippingRequests"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserShippingRequests />
              </ProtectComponent>
            }
          />
          <Route
            path="/tabao1688"
            element={
              <ProtectComponent key={location.pathname}>
                <Tabao1688 />
              </ProtectComponent>
            }
          />
          <Route
            path="/productsList"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserProductsList />
              </ProtectComponent>
            }
          />
          {/* Store integrations. Same shape as every other protected route:
              nested under the AppLayout route and wrapped in ProtectComponent. */}
          <Route
            path="/integrations"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <IntegrationsHome />
              </ProtectComponent>
            }
          />
          {/* One store's page, its tab in the path. Keyed on the STORE, not the
              whole path: switching tabs must not remount the page. */}
          <Route
            path="/integrations/stores/:channelId/:tab?"
            element={
              <ProtectComponent key={location.pathname.split("/").slice(0, 4).join("/")} doNotCheckMonthlyPayment={true}>
                <StorePage />
              </ProtectComponent>
            }
          />
          {/* "Open this tab of the right store", for links that don't know the
              store: dashboard alerts, notifications, the digest email. */}
          <Route
            path="/integrations/go/:tab"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <StoreTabRedirect />
              </ProtectComponent>
            }
          />
          {/* The pages the store page replaced, so old emails and bookmarks
              still land on the right tab. */}
          {[
            ["listings", "listings"],
            ["drafts", "listings"],
            ["mapping", "products"],
            ["quarantine", "orders"],
            ["activity", "problems"],
          ].map(([oldPath, tab]) => (
            <Route
              key={oldPath}
              path={`/integrations/${oldPath}`}
              element={
                <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                  <StoreTabRedirect tab={tab} />
                </ProtectComponent>
              }
            />
          ))}
          {/* Store fixes: one card per platform, each opening its problem guide. */}
          <Route
            path="/integrations/fixes"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <StoreFixes />
              </ProtectComponent>
            }
          />
          {/* The store problem guide. "How to fix →" links add #<problem-id>. */}
          <Route
            path="/integrations/help/:platform"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <StoreProblemsGuide />
              </ProtectComponent>
            }
          />
          <Route
            path="/newClient"
            element={
              <ProtectComponent key={location.pathname}>
                <NewUserClient />
              </ProtectComponent>
            }
          />
          <Route
            path="/clientsList"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserClientsList />
              </ProtectComponent>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <Notifications />
              </ProtectComponent>
            }
          />
          <Route
            path="/sellerHistory"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <SellerHistoryPage />
              </ProtectComponent>
            }
          />
          <Route
            path="/userSourcingRestockRequests"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserSourcingRestockRequests />
              </ProtectComponent>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <UserProfile />
              </ProtectComponent>
            }
          />
          {/* Readable with a lapsed subscription too: it explains how to renew. */}
          <Route
            path="/help"
            element={
              <ProtectComponent key={location.pathname} doNotCheckMonthlyPayment={true}>
                <HelpGuide />
              </ProtectComponent>
            }
          />
        </Route>
  
        {/* Catch-all 404 outside the main layout wrapper */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
}

export default Routing;